import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { SlidersHorizontal, Search, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { GridColumnDef } from './grid-columns';

interface ColumnPickerProps {
  allColumns: GridColumnDef[];
  columnOrder: string[];
  hiddenColumns: Set<string>;
  onOrderChange: (order: string[]) => void;
  onVisibilityChange: (hidden: Set<string>) => void;
  onReset: () => void;
  isCustomized: boolean;
}

function SortableColumnRow({
  col,
  visible,
  onToggle,
}: {
  col: GridColumnDef;
  visible: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-nightshade-50 dark:hover:bg-nightshade-800"
    >
      <input
        type="checkbox"
        checked={visible}
        onChange={onToggle}
        className="w-3.5 h-3.5 rounded border-nightshade-300 dark:border-nightshade-600 text-blueberry-500 focus:ring-blueberry-400 shrink-0"
      />
      <span className="text-xs text-nightshade-700 dark:text-nightshade-300 flex-1 truncate">
        {col.title}
      </span>
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-nightshade-400 hover:text-nightshade-600 dark:hover:text-nightshade-300 shrink-0"
        tabIndex={-1}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function ColumnPicker({
  allColumns,
  columnOrder,
  hiddenColumns,
  onOrderChange,
  onVisibilityChange,
  onReset,
  isCustomized,
}: ColumnPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Build ordered list: view columns first (in order), then remaining columns alphabetically
  const orderedColumns = useMemo(() => {
    const byId = new Map(allColumns.map(c => [c.id, c]));
    const inOrder = columnOrder.map(id => byId.get(id)).filter((c): c is GridColumnDef => c != null);
    const inOrderIds = new Set(columnOrder);
    const rest = allColumns.filter(c => !inOrderIds.has(c.id)).sort((a, b) => a.title.localeCompare(b.title));
    return [...inOrder, ...rest];
  }, [allColumns, columnOrder]);

  const filtered = useMemo(() => {
    if (!search.trim()) return orderedColumns;
    const q = search.toLowerCase();
    return orderedColumns.filter(c => c.title.toLowerCase().includes(q));
  }, [search, orderedColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(String(active.id));
    const newIndex = columnOrder.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onOrderChange(arrayMove(columnOrder, oldIndex, newIndex));
  }, [columnOrder, onOrderChange]);

  const handleToggle = useCallback((colId: string) => {
    const inOrder = columnOrder.includes(colId);
    if (!inOrder) {
      // Column not in current order — add it and make visible
      onOrderChange([...columnOrder, colId]);
      const next = new Set(hiddenColumns);
      next.delete(colId);
      onVisibilityChange(next);
    } else {
      const next = new Set(hiddenColumns);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      onVisibilityChange(next);
    }
  }, [hiddenColumns, columnOrder, onVisibilityChange, onOrderChange]);

  const visibleCount = columnOrder.filter(id => !hiddenColumns.has(id)).length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border transition-colors ${
          isCustomized
            ? 'border-blueberry-300 dark:border-blueberry-600 text-blueberry-600 dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20'
            : 'border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
        }`}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
        Columns
        {isCustomized && <span className="w-1.5 h-1.5 rounded-full bg-blueberry-500" />}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-30 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-xl shadow-lg w-64 max-h-96 flex flex-col">
          <div className="p-2 border-b border-nightshade-100 dark:border-nightshade-800">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-nightshade-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search columns..."
                className="w-full text-xs bg-nightshade-50 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg pl-7 pr-2 py-1.5 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-nightshade-400">{visibleCount} of {columnOrder.length} visible</span>
              {isCustomized && (
                <button
                  onClick={() => { onReset(); }}
                  className="text-[10px] text-blueberry-500 hover:text-blueberry-600"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-1">
            {search.trim() ? (
              // When searching, don't allow drag reorder (filtered list order ≠ full order)
              filtered.map(col => (
                <div
                  key={col.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-nightshade-50 dark:hover:bg-nightshade-800"
                >
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(col.id)}
                    onChange={() => handleToggle(col.id)}
                    className="w-3.5 h-3.5 rounded border-nightshade-300 dark:border-nightshade-600 text-blueberry-500 focus:ring-blueberry-400 shrink-0"
                  />
                  <span className="text-xs text-nightshade-700 dark:text-nightshade-300 flex-1 truncate">
                    {col.title}
                  </span>
                </div>
              ))
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedColumns.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  {orderedColumns.map(col => (
                    <SortableColumnRow
                      key={col.id}
                      col={col}
                      visible={!hiddenColumns.has(col.id)}
                      onToggle={() => handleToggle(col.id)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
